import { useCallback, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { reportMutationError } from '@/lib/mutationError';
import { tokenStore } from '@lib/tokenStore';
import { useConfirmPasswordResetMutation, useGetPasswordPolicyQuery } from '../api';
import { buildPasswordSchema } from '../lib/passwordPolicy';
import { PasswordRequirements } from './PasswordRequirements';

interface ResetPasswordFormProps {
  /** Reset token from the `/forgot-password/:token` route (the email link). */
  token: string;
}

interface ResetFormValues {
  password: string;
  confirmPassword: string;
}

/** True when an RTK Query error is an HTTP 404 (invalid/expired reset token). */
function isInvalidToken(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status?: unknown }).status === 404
  );
}

/**
 * Reset-password form rendered for `/forgot-password/:token`. Faithful port of
 * the legacy token branch: new + confirm password fields, a single show/hide
 * toggle that reveals BOTH fields together, and a live requirements checklist
 * driven by the server password policy. On success it toasts and redirects to
 * `/signin` (legacy behavior); a 404 (expired or already-used link) surfaces an
 * actionable message telling the user to request a new reset link.
 */
export function ResetPasswordForm({ token }: Readonly<ResetPasswordFormProps>): JSX.Element {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { data: policy } = useGetPasswordPolicyQuery();
  const [confirmReset, { isLoading }] = useConfirmPasswordResetMutation();

  // The password rules are the shared `buildPasswordSchema`; we compose the
  // confirm-password "must match" check on top. zod 4's ZodType<Output, Input>
  // defaults Input to unknown; zodResolver needs it stated. This schema doesn't
  // transform, so input === output.
  const schema = useMemo(
    () =>
      z
        .object({
          password: buildPasswordSchema(policy, t),
          confirmPassword: z.string().min(1, t('resetPassword.errors.confirmRequired')),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('resetPassword.errors.mustMatch'),
          path: ['confirmPassword'],
        }) as z.ZodType<ResetFormValues, ResetFormValues>,
    [policy, t],
  );
  // The policy loads async, so the schema changes after first render. Keep it
  // in a ref and wrap zodResolver in a stable callback that reads the latest —
  // react-hook-form captures the resolver once, so passing a fresh schema
  // directly would never take effect.
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  // zod 4 + resolvers 5 type the resolver over the schema's input/output pair,
  // which no longer unifies with our single form-values type — cast scoped to
  // the resolver only (repo convention, see CreateEditReasonModal).
  const resolver = useCallback<Resolver<ResetFormValues>>(
    (values, context, options) =>
      (zodResolver(schemaRef.current) as unknown as Resolver<ResetFormValues>)(
        values,
        context,
        options,
      ),
    [],
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetFormValues>({
    resolver,
    defaultValues: { password: '', confirmPassword: '' },
  });

  const passwordValue = watch('password') ?? '';

  const onSubmit = handleSubmit(async ({ password }) => {
    try {
      await confirmReset({ token, password }).unwrap();
      // The backend now revokes all Keycloak sessions/refresh tokens on a
      // password reset. If the reset link was opened in a browser
      // that still holds a logged-in session, those local tokens are now dead
      // server-side — drop them here so we don't leave a zombie session that
      // lingers until the access token expires. Normally the reset page is
      // anonymous and there's nothing to clear, so this is a safe no-op.
      tokenStore.clearAll();
      toast.success(t('resetPassword.success'));
      navigate('/signin');
    } catch (error) {
      if (isInvalidToken(error)) {
        toast.error(t('resetPassword.errors.invalidToken'));
      } else {
        reportMutationError(error);
      }
    }
  });

  const toggleLabel = showPassword ? t('signIn.hidePassword') : t('signIn.showPassword');

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="reset-password-form" noValidate>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="reset-password-new" className="text-sm font-medium">
          {t('resetPassword.newPassword')}
        </label>
        <div className="relative">
          <Input
            id="reset-password-new"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            autoFocus
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'reset-password-new-error' : undefined}
            data-sentry-mask="true"
            data-testid="reset-password-new"
            className="pr-10"
            {...register('password')}
          />
          {/* Single toggle reveals BOTH password fields together (legacy parity). */}
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={toggleLabel}
            aria-pressed={showPassword}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="reset-password-toggle"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password && (
          <p id="reset-password-new-error" className="text-xs text-destructive" data-testid="reset-password-new-error">
            {errors.password.message}
          </p>
        )}
      </div>

      <PasswordRequirements password={passwordValue} policy={policy} showInfo />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="reset-password-confirm" className="text-sm font-medium">
          {t('resetPassword.confirmPassword')}
        </label>
        <div className="relative">
          <Input
            id="reset-password-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.confirmPassword)}
            aria-describedby={errors.confirmPassword ? 'reset-password-confirm-error' : undefined}
            data-sentry-mask="true"
            data-testid="reset-password-confirm"
            className="pr-10"
            {...register('confirmPassword')}
          />
          {/* Second toggle shares the same visibility state — legacy showed an
              eye in both fields, and clicking either reveals both. */}
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={toggleLabel}
            aria-pressed={showPassword}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="reset-password-confirm-toggle"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p
            id="reset-password-confirm-error"
            className="text-xs text-destructive"
            data-testid="reset-password-confirm-error"
          >
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      <Button type="submit" disabled={isLoading} data-testid="reset-password-submit">
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('resetPassword.submitting')}
          </>
        ) : (
          t('resetPassword.submit')
        )}
      </Button>
    </form>
  );
}
